import { createSlice } from '@reduxjs/toolkit';
import { SideOrderT } from '../../@types/sideOrder.type';

type initialStateT = {
  clearLimitedByMaximumSelectedMeals: boolean;
  error: string;
  list: Record<string, Record<number, SideOrderT>>;
  numberOfSideOrders: number;
  priceOfSideOrders: number;
};

const initialState: initialStateT = {
  clearLimitedByMaximumSelectedMeals: false,
  error: '',
  list: {},
  numberOfSideOrders: 0,
  priceOfSideOrders: 0,
};

const reduceListToNumberAndPriceOfSideOrders = (
  obj: Record<string, Record<number, SideOrderT>>
) =>
  Object.values(obj).reduce(
    (
      acc: {
        numberOfSideOrders: number;
        priceOfSideOrders: number;
      },
      curr
    ) => {
      const filter = Object.values(curr).filter(item => item.amount);

      if (filter.length) {
        acc.priceOfSideOrders += filter.reduce(
          (sum, li) => sum + li.defaultPrice * li.amount,
          0
        );
        acc.numberOfSideOrders += filter.reduce(
          (sum, li) => sum + li.amount,
          0
        );
      }

      return acc;
    },
    {
      numberOfSideOrders: 0,
      priceOfSideOrders: 0,
    }
  );

const chooseSideOrdersSlice = createSlice({
  initialState,
  name: 'chooseSideOrders',
  reducers: {
    addSingleSideOrder: (state, action) => {
      const { day, sideOrder } = action.payload;
      const { possibleSideOrderId } = sideOrder;

      if (!state?.list?.[day]?.[possibleSideOrderId]) {
        state.list = {
          ...state.list,
          [day]: {
            ...state.list[day],
            [possibleSideOrderId]: sideOrder,
          },
        };
      } else {
        // @ts-ignore TODO
        state.list[day][possibleSideOrderId] = sideOrder;
      }
    },
    changeSingleSideOrder: (state, action) => {
      const { day, sideOrder } = action.payload;
      const { amount, possibleSideOrderId } = sideOrder;

      // @ts-ignore TODO
      state.list[day][possibleSideOrderId].amount = amount;
    },
    changeLimitedByMaximumSelectedMeals: (state, action) => {
      state.clearLimitedByMaximumSelectedMeals = action.payload;
    },
    clearSideOrderError(state) {
      state.error = '';
    },
    resetSideOrderState: () => initialState,
    triggerSideOrderError(state, action) {
      state.error = action.payload;
    },
    updateSideOrder(state, action) {
      state.list = action.payload;

      const reducedList = reduceListToNumberAndPriceOfSideOrders(
        action.payload
      );

      state.priceOfSideOrders = reducedList.priceOfSideOrders;
      state.numberOfSideOrders = reducedList.numberOfSideOrders;
    },
    storeSideOrdersSlice(state, action) {
      const reducedSideOrders = Object.keys(action.payload).reduce(
        (acc1: Record<string, Record<number, SideOrderT>>, curr1) => {
          acc1[curr1] = action.payload[curr1].reduce(
            (acc2: Record<number, SideOrderT>, curr2: SideOrderT) => {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              acc2[curr2.possibleSideOrderId] = <SideOrderT>{
                defaultPrice: curr2.defaultPrice,
                limitedByMaximumSelectedMeals:
                  curr2.limitedByMaximumSelectedMeals,
                maxQuantity: curr2.maxQuantity,
                minQuantity: curr2.minQuantity,
                amount: curr2.amount,
                name: curr2.name,
                possibleSideOrderId: curr2.possibleSideOrderId,
              };
              return acc2;
            },
            {}
          );
          return acc1;
        },
        {}
      );

      const reducedList =
        reduceListToNumberAndPriceOfSideOrders(reducedSideOrders);

      state.list = reducedSideOrders;
      state.priceOfSideOrders = reducedList.priceOfSideOrders;
      state.numberOfSideOrders = reducedList.numberOfSideOrders;
    },
  },
});

export const {
  addSingleSideOrder,
  changeLimitedByMaximumSelectedMeals,
  changeSingleSideOrder,
  clearSideOrderError,
  resetSideOrderState,
  storeSideOrdersSlice,
  triggerSideOrderError,
  updateSideOrder,
} = chooseSideOrdersSlice.actions;

export default chooseSideOrdersSlice.reducer;
